// CLIENT PORTAL RESTYLE — display formatting.
//
// ⚠️ MONEY IS NATIVE CURRENCY ONLY, always with its symbol. The agency
// display-currency conversion is an internal cross-portfolio tool; a client sees
// their own currency, unconverted, with no selector. Nothing here may import
// the fx machinery.
//
// ⚠️ AN UNMEASURED FIGURE IS AN EM DASH, NEVER A ZERO — the whole report's rule,
// applied at the render edge. Each formatter below has a `orDash` twin so a
// caller cannot forget.

import type { ChartMetric, ChartScale } from '../../../../../lib/portalReport/chartPlan';

const LOCALE = 'en-AU';

export function moneyFull(amount: number, currency: string): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function moneyRound(amount: number, currency: string): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Axis labels are short — "$9k" rather than "A$9,000.00".
 *
 * ⚠️ `decimals` comes from the axis STEP, not from a fixed rule. A cost-per-click
 * axis stepping by 0.5 printed at zero decimals reads `0 · 1 · 1 · 2 · 2 · 3` —
 * duplicated labels, which is what a client saw. Abbreviation where the
 * magnitude is large, decimals where the range is small; the step is the only
 * thing that knows which.
 */
export function moneyAxis(amount: number, currency: string, scale?: ChartScale): string {
  const symbol = currencySymbol(currency);
  const step = scale?.step ?? 1;
  // ⚠️⚠️ THE WHOLE AXIS ABBREVIATES OR NONE OF IT DOES, decided by the axis MAX
  // rather than by each value. Deciding per value gave one axis two units
  // ("$900 · $950 · $1k"), which reads as a different measure halfway up.
  const abbreviate = (scale ? Math.abs(scale.max) : Math.abs(amount)) >= 1000;
  if (abbreviate) {
    // ⚠️ The abbreviation has its own precision problem: at a step under $100, a
    // one-decimal "$1.1k" duplicates its neighbour. The step scales down with
    // the value, so the decimals must too.
    return `${symbol}${(amount / 1000).toFixed(decimalsForStep(step / 1000))}k`;
  }
  return `${symbol}${amount.toFixed(decimalsForStep(step))}`;
}

function currencySymbol(currency: string): string {
  const parts = new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value ?? '$';
}

/** One decimal place, thousands-separated — for FRACTIONAL counts (Google conversions). */
export function oneDecimal(n: number): string {
  return new Intl.NumberFormat(LOCALE, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(n);
}

export function numberFull(n: number): string {
  return new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 0 }).format(n);
}

/**
 * ⚠️ THOUSANDS-SEPARATED, and honouring the step's decimals. It used to be
 * `String(Math.round(n))`, which both dropped the separators an impressions axis
 * needs and duplicated adjacent labels on any fractional step.
 */
export function numberAxis(n: number, scale?: ChartScale): string {
  const decimals = decimalsForStep(scale?.step ?? 1);
  return new Intl.NumberFormat(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
}

// ---------------------------------------------------------------------------
// ⚠️⚠️ ONE FORMATTER PER METRIC — the figure, the axis and the tooltip
// ---------------------------------------------------------------------------
//
// The number above a chart already rendered correctly (`1.34%`, `$7.93`) while
// the axis and the tooltip ran through a second, integer formatter — so a
// cost-per-click axis read `0 · 1 · 1 · 2 · 2 · 3` and a tooltip said `1` for a
// point of 1.05. An axis that rounds where the headline does not is the same
// class of defect as a chart that plots a rate disagreeing with its own figure;
// the fix is the same one `valueAt` got — ONE derivation, one presentation.
//
// ⚠️ `axis` takes THE WHOLE SCALE, not a decimal count. A formatter needs the
// STEP to know how many decimals keep consecutive ticks distinct, and the MAX to
// decide whether the axis abbreviates at all — and it has to be the same answer
// for every tick, or one axis carries two units. `value` takes neither: a
// headline figure and a tooltip show the metric's own full precision, and it is
// only the tick labels that have to fit a space.

/** The one thing that decides how many decimals a tick needs. */
export function decimalsForStep(step: number): number {
  if (!Number.isFinite(step) || step <= 0) return 0;
  // A step of 0.5 needs one decimal; 0.025 needs two. Capped, because past three
  // an axis label is longer than the space it has.
  return Math.min(3, Math.max(0, Math.ceil(-Math.log10(step))));
}

export interface MetricFormat {
  /** The headline figure and the tooltip — the metric's own precision. */
  value: (v: number) => string;
  /** One axis tick, at the precision this scale's step demands. */
  axis: (v: number, scale: ChartScale) => string;
}

/**
 * ⚠️ TOTAL over `ChartMetric` for the same reason `METRIC_LABELS` is: a new
 * metric must be a compile error rather than falling through to an integer
 * formatter, which is precisely the bug this replaces.
 */
const METRIC_FORMATS: Record<ChartMetric, (currency: string) => MetricFormat> = {
  leads: () => COUNT,
  impressions: () => COUNT,
  clicks: () => COUNT,
  // ⚠️ Two decimals, matching the figure the delivery band prints ("1.34%").
  ctr: () => percentFormat(2),
  // The share chart has always shown one, and its axis is the fixed 0-100.
  share: () => percentFormat(1),
  spend: moneyFormat,
  costPerLead: moneyFormat,
  costPerThousand: moneyFormat,
  costPerClick: moneyFormat,
  // ⚠️ The CRM six. Counts are counts whatever recorded them; the close rate is
  // a percentage at ONE decimal, matching `deriveConversionRate`'s own rounding
  // — a formatter showing two would print a precision the figure does not have.
  crmLeads: () => COUNT,
  crmDigitalLeads: () => COUNT,
  crmStaffLeads: () => COUNT,
  crmSales: () => COUNT,
  crmCloseRate: () => percentFormat(1),
  crmCostPerDigitalLead: moneyFormat,
  crmCostPerSale: moneyFormat,
  webSessions: () => COUNT,
  webNewUsers: () => COUNT,
  // ⚠️ One decimal: bounce is derived from two sums, and a second decimal would
  // print a precision the figure does not have.
  webBounceRate: () => percentFormat(1),
  webAvgDuration: () => DURATION,
};

/** Seconds as `m:ss` (or `h:mm:ss`). The axis rounds to whole seconds. */
export function durationText(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`;
}

const DURATION: MetricFormat = {
  value: (v) => durationText(v),
  axis: (v) => durationText(v),
};

const COUNT: MetricFormat = {
  value: (v) => numberFull(v),
  axis: (v, scale) => numberAxis(v, scale),
};

function percentFormat(places: number): MetricFormat {
  return {
    value: (v) => `${v.toFixed(places)}%`,
    axis: (v, scale) => `${v.toFixed(decimalsForStep(scale.step))}%`,
  };
}

function moneyFormat(currency: string): MetricFormat {
  return {
    value: (v) => moneyFull(v, currency),
    axis: (v, scale) => moneyAxis(v, currency, scale),
  };
}

export function metricFormat(metric: ChartMetric, currency: string): MetricFormat {
  return METRIC_FORMATS[metric](currency);
}

export const DASH = '—';

export function orDash<T>(v: T | null | undefined, render: (value: T) => string): string {
  return v === null || v === undefined ? DASH : render(v);
}

/** "1 Aug 2026" — the report states dates in full, never as YYYY-MM-DD. */
export function formatReportDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(Date.UTC(y, m - 1, d));
}

/**
 * A TIMESTAMP, as a date and time.
 *
 * ⚠️ Distinct from `formatReportDate`, which takes a YYYY-MM-DD. Passing an ISO
 * INSTANT to that one renders the raw string back at the reader — caught in the
 * browser, where the audience snapshot line read "last pulled
 * 2026-08-30T02:30:00Z".
 *
 * ⚠️ Rendered in the READER’S own timezone, deliberately: this is the one
 * figure on the page that answers "how fresh is this", and a UTC instant makes
 * a client do arithmetic to find out.
 */
export function formatReportInstant(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(t);
}

/**
 * A percentage, one decimal.
 *
 * ⚠️ Takes an already-0-to-100 value, matching what the wire carries. Passing a
 * 0-to-1 ratio here would render 0.6% for 63%, which is the sort of wrong that
 * looks like a bad month rather than a bug.
 */
export function percent(pct: number): string {
  return `${pct.toFixed(1)}%`;
}
